/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Input,
  Select,
  ListItem,
  Setters,
  Errors,
  Button,
  Typography,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { Divider } from 'components';
import { deleteMerchant } from 'tabs/Merchants/api';
import { Merchant, MerchantStatus } from 'tabs/types';
import { COUNTRY_OPTIONS, createPercentageFee, createFixedFee } from 'utils';
import { MerchantConfig } from './components';

export const DEFAULT_ONRAMP_CURRENCY_CODE = 'BTC';

interface Props {
  formFields: Merchant;
  formErrors: Errors<Merchant>;
  formSetters: Setters<Merchant>;
  onSuccess: (message: string) => void;
  editable: boolean;
  hasError: boolean;
  hasBeenEdited: boolean;
  loading: boolean;
  onSave: () => void;
}

export const MerchantForm = ({
  formFields,
  formErrors,
  formSetters,
  onSuccess,
  editable,
  hasError,
  hasBeenEdited,
  loading,
  onSave,
}: Props) => {
  const navigate = useNavigate();
  const { merchantId } = useParams();
  const isNewMerchant = merchantId === 'new';
  const styles = useStyles();

  const [showCustomerFeePrompt, setShowCustomerFeePrompt] = useState(
    formFields.customerFee === null
  );

  useEffect(() => {
    setShowCustomerFeePrompt(formFields.customerFee === null);
  }, [formFields.customerFee]);

  const [{ loading: deleteMerchantLoading }, deleteMerchantFn] =
    useAsyncFn(async () => {
      const r = await deleteMerchant(formFields.uuid ?? '');
      onSuccess('Merchant deleted');
      void navigate('/merchants');
      return r;
    }, [formFields.uuid]);

  return (
    <>
      <form css={[styles.container, styles.inputWrapper]}>
        {!isNewMerchant && (
          <>
            <Input
              label="Merchant ID"
              value={formFields.uuid || ''}
              onChange={() => null}
              disabled
            />
            <Input
              label="User ID"
              value={formFields.userId || ''}
              onChange={() => null}
              disabled
            />
            <Select
              label="Status"
              options={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Blocked', value: 'BLOCKED' },
              ]}
              disabled={!editable}
              values={[formFields.status]}
              onSelect={(e) => formSetters.status.set(e[0] as MerchantStatus)}
            />
          </>
        )}
        <Input
          label="Internal Name"
          value={formFields.name}
          disabled={!editable}
          onChange={(e) => formSetters.name.set(e.target.value)}
          helperText={
            formErrors.name ?? 'This is the internal name of the merchant.'
          }
          error={!!formErrors.name}
        />
        <Input
          label="Display Name"
          value={formFields.displayName}
          disabled={!editable}
          onChange={(e) => formSetters.displayName.set(e.target.value)}
          helperText={
            formErrors.displayName ??
            'This is the display name of the merchant. It will be shown in the checkout pages.'
          }
          error={!!formErrors.displayName}
        />
        <Select
          label="Country"
          options={COUNTRY_OPTIONS}
          values={[formFields.countryCode]}
          disabled={!editable}
          onSelect={([selected]) => formSetters.countryCode.set(selected)}
        />
        <Input
          label="IBAN"
          value={formFields.paymentDetails?.iban ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.paymentDetails?.set({
              iban: e.target.value,
              bic: formFields.paymentDetails?.bic ?? '',
              reference: formFields.paymentDetails?.reference ?? 'Quickbit',
            })
          }
          helperText={
            formErrors.paymentDetails?.iban ??
            'Please double check that the IBAN is correct. There is no validation done for this field.'
          }
          error={!!formErrors.paymentDetails?.iban}
        />
        <Input
          label="BIC"
          value={formFields.paymentDetails?.bic ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.paymentDetails?.set({
              iban: formFields.paymentDetails?.iban ?? '',
              bic: e.target.value,
              reference: formFields.paymentDetails?.reference ?? 'Quickbit',
            })
          }
          helperText={
            formErrors.paymentDetails?.bic ??
            'Please double check that the BIC is correct. There is no validation done for this field.'
          }
          error={!!formErrors.paymentDetails?.bic}
        />
        <Input
          label="Payment reference"
          value={formFields.paymentDetails?.reference ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.paymentDetails?.set({
              iban: formFields.paymentDetails?.iban ?? '',
              bic: formFields.paymentDetails?.bic ?? '',
              reference: e.target.value,
            })
          }
          helperText={formErrors.paymentDetails?.reference}
          error={!!formErrors.paymentDetails?.reference}
        />
        <Typography variant="bodyBold">Fees</Typography>
        <Input
          label="Fee for Pay orders"
          type="number"
          value={formFields.payFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.payFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.payFee.value}
          helperText={formErrors.payFee.value}
        />
        <Input
          label="Fee for Onramp (Top up) orders"
          type="number"
          value={formFields.onrampFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.onrampFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.onrampFee.value}
          helperText={formErrors.onrampFee.value}
        />
        <Input
          label="Fee for Processing orders"
          type="number"
          value={formFields.processingFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.processingFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.processingFee.value}
          helperText={formErrors.processingFee.value}
        />
        <Input
          label="Gateway fee"
          type="number"
          value={formFields.gatewayFee.value.toString()}
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.gatewayFee.value.set(
              createFixedFee(e.target.value).value
            )
          }
          error={!!formErrors.gatewayFee.value}
          helperText={formErrors.gatewayFee.value}
        />
        <Input
          label="Settlement fee"
          type="number"
          value={formFields.settlementFee.value.toString()}
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.settlementFee.value.set(
              createFixedFee(e.target.value).value
            )
          }
          error={!!formErrors.settlementFee.value}
          helperText={formErrors.settlementFee.value}
        />
        {showCustomerFeePrompt && (
          <ListItem
            css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
            title="No customer fee"
            action="%"
            onClick={() => {
              setShowCustomerFeePrompt(false);
              formSetters.customerFee.set(createPercentageFee('0'));
            }}
            disabled={!editable}
          />
        )}
        {formFields.customerFee && (
          <Input
            label="Customer fee"
            type="number"
            endAdornment="%"
            value={formFields.customerFee.value.toString()}
            disabled={!editable}
            onChange={(e) =>
              formSetters.customerFee.value?.set(
                createPercentageFee(e.target.value).value
              )
            }
            error={!!formErrors.customerFee?.value}
            helperText={formErrors.customerFee?.value}
          />
        )}
        <Typography variant="bodyBold">CW Fees</Typography>
        <Input
          label="Corporate walllet receive external fee"
          type="number"
          value={formFields.corporateWalletReceiveExternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.corporateWalletReceiveExternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.corporateWalletReceiveExternalFee.value}
          helperText={formErrors.corporateWalletReceiveExternalFee.value}
        />
        <Input
          label="Corporate walllet receive external fee minimum applicable amount"
          type="number"
          value={formFields.corporateWalletReceiveExternalFeeMinimumApplicableAmount.toString()}
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.corporateWalletReceiveExternalFeeMinimumApplicableAmount.set(
              parseFloat(e.target.value)
            )
          }
          error={!!formErrors.corporateWalletReceiveExternalFee.value}
          helperText={formErrors.corporateWalletReceiveExternalFee.value}
        />
        <Input
          label="Corporate wallet send internal fee"
          type="number"
          value={formFields.corporateWalletSendInternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.corporateWalletSendInternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.corporateWalletSendInternalFee.value}
          helperText={formErrors.corporateWalletSendInternalFee.value}
        />
        <Input
          label="Corporate wallet send external fee"
          type="number"
          value={formFields.corporateWalletSendExternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.corporateWalletSendExternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.corporateWalletSendExternalFee.value}
          helperText={formErrors.corporateWalletSendExternalFee.value}
        />
        <MerchantConfig
          editable={editable}
          formErrors={formErrors.config}
          formFields={formFields.config}
          formSetters={formSetters.config}
        />
        {!isNewMerchant && formFields.deletedAt && (
          <Input
            label="Deleted At"
            value={formFields.deletedAt.toLocaleString() || ''}
            onChange={() => null}
            disabled
          />
        )}
        {!isNewMerchant && (
          <>
            <Divider />
            <ListItem
              title="Remove merchant"
              css={[
                styles.listItemBorder,
                !editable && styles.listItemDisabled,
              ]}
              icon={deleteMerchantLoading ? 'spinner' : 'trash'}
              onClick={() => deleteMerchantFn()}
              disabled={!editable}
            />
          </>
        )}
        {!isNewMerchant && formFields.uuid && (
          <Button
            text="Save"
            onClick={onSave}
            loading={loading}
            disabled={!editable || hasError || !hasBeenEdited || loading}
          />
        )}
      </form>
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  container: {
    background: palette.white,
    borderRadius: shape.borderRadius,
    gridColumn: 'span 1',
    gridRow: 'span 2',
  },
  inputWrapper: {
    marginBottom: 'auto',
    padding: spacing.xxs,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.xxs,
    border: `1px solid ${palette.grey.x100}`,
  },
  listItemBorder: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
}));
